export const FOUNDATION_TERMS = [
    // ---------------------------------------------------------------- foundations
    {
        id: 'insured',
        term: 'Insured',
        category: 'foundations',
        essential: true,
        def: 'Anyone the policy protects. A policy can cover several insureds — the business itself, its owners, its employees — and each may be covered for different things.',
        detail: 'Who counts as an insured is decided by the "Who Is An Insured" section of the form, not by who is listed on the declarations. An employee is usually an insured for acts within the scope of employment without ever being named.',
        confused: 'Not the same as the named insured. Every named insured is an insured; most insureds are not named.',
        see: ['named-insured', 'additional-insured', 'first-named-insured'],
    },
    {
        id: 'named-insured',
        term: 'Named insured',
        category: 'foundations',
        essential: true,
        def: 'The person or business actually written on the declarations page. They hold the contract, pay the premium, and get the broadest set of rights under it.',
        detail: 'Getting the named insured wrong is one of the most common and most expensive errors on a commercial policy: the wrong entity, a d/b/a instead of the LLC, or an operating company named while the real-estate holding company that owns the building is left off.',
        see: ['first-named-insured', 'insured', 'declarations'],
    },
    {
        id: 'first-named-insured',
        term: 'First named insured',
        category: 'policy',
        def: 'When several entities are named, the first one listed. It carries specific duties and rights the others do not.',
        detail: 'The first named insured is the only one entitled to receive cancellation notice, the only one who can request cancellation, the one responsible for premium, and the one who receives any return premium. On a policy covering six related LLCs, the other five get told nothing.',
        see: ['named-insured', 'cancellation', 'return-premium'],
    },
    {
        id: 'insurer',
        term: 'Insurer',
        short: 'Carrier',
        aka: ['carrier', 'underwriting company', 'markets'],
        category: 'foundations',
        essential: true,
        def: 'The company that takes on the risk and pays claims. In conversation, "carrier" and "market" are used interchangeably with insurer.',
        detail: 'A "market" can also mean a wholesaler or MGA that fronts access to a carrier, so "we have a market for that" does not always mean direct access to paper.',
        see: ['mga', 'wholesale-broker', 'paper'],
    },
    {
        id: 'policy',
        term: 'Policy',
        category: 'foundations',
        essential: true,
        def: 'The written contract between the insured and the insurer. It states what is covered, for how much, for how long, and under what conditions.',
        detail: 'A policy is the whole assembled package: jacket, declarations, coverage forms, and every endorsement. A "coverage form" on its own is not a policy, which is why a quote showing only limits tells you very little.',
        see: ['declarations', 'coverage-form', 'endorsement', 'policy-jacket'],
    },
    {
        id: 'premium',
        term: 'Premium',
        category: 'foundations',
        essential: true,
        def: 'What the insured pays for the coverage.',
        detail: 'Premium is not the whole cost of a policy. Taxes, stamping fees, policy fees, inspection fees and broker fees can add several percent, and on surplus lines business those are usually not commissionable.',
        see: ['taxes-and-fees', 'earned-premium', 'minimum-earned-premium', 'commission'],
    },
    {
        id: 'risk',
        term: 'Risk',
        category: 'foundations',
        essential: true,
        def: 'Two meanings, both common. Formally, the chance of loss. Conversationally, the account itself — "we looked at that risk last month".',
        see: ['exposure', 'hazard', 'peril'],
    },
    {
        id: 'exposure',
        term: 'Exposure',
        category: 'foundations',
        essential: true,
        def: 'Something about the business that could produce a loss — a fleet of vans, a hundred employees, a building, a product sold to the public.',
        detail: 'Exposure is also the measurable basis a premium is charged on: payroll, sales, square footage, vehicle count. "Exposure basis" and "rating basis" mean the same thing.',
        see: ['rating-basis', 'hazard', 'peril', 'exposure-analysis'],
    },
    {
        id: 'peril',
        term: 'Peril',
        category: 'foundations',
        essential: true,
        def: 'The cause of a loss — fire, theft, wind, water damage, collision.',
        confused: 'A peril is what happens. A hazard is what makes it more likely to happen.',
        see: ['hazard', 'named-perils', 'special-form'],
    },
    {
        id: 'hazard',
        term: 'Hazard',
        category: 'foundations',
        def: 'A condition that makes a loss more likely or more severe. Oily rags near a heater are a fire hazard; the fire is the peril.',
        detail: 'Underwriters split these three ways. Physical hazard is the condition itself. Moral hazard is dishonesty — an insured who would welcome a loss. Morale hazard is carelessness that comes from being insured.',
        see: ['peril', 'moral-hazard', 'loss-control'],
    },
    {
        id: 'moral-hazard',
        term: 'Moral hazard',
        category: 'underwriting',
        def: 'The risk that being insured changes how someone behaves — at the extreme, an insured with a failing business and a well-insured building.',
        confused: 'Moral hazard is dishonesty. Morale hazard is mere carelessness — leaving the shop unlocked because "it is covered anyway".',
        see: ['hazard', 'adverse-selection'],
    },
    {
        id: 'loss',
        term: 'Loss',
        category: 'foundations',
        essential: true,
        def: 'The damage, injury, or liability the insured suffers. Also used for what the carrier pays: "the loss was $40,000".',
        see: ['claim', 'occurrence', 'loss-run'],
    },
    {
        id: 'claim',
        term: 'Claim',
        category: 'claims',
        essential: true,
        def: 'A demand for payment under the policy — by the insured for their own loss, or by a third party who says the insured hurt them.',
        detail: 'On claims-made policies the definition of "claim" in the form is doing real work: whether a subpoena, a regulatory inquiry, or a demand letter counts as a claim decides whether reporting it triggers coverage or wastes a retention.',
        see: ['claims-made', 'occurrence', 'fnol', 'notice'],
    },
    {
        id: 'indemnity',
        term: 'Indemnity',
        category: 'foundations',
        def: 'The principle that insurance restores you to where you were before the loss — no better. It is why replacement cost policies still require you to actually replace the property.',
        see: ['acv', 'replacement-cost', 'insurable-interest'],
    },
    {
        id: 'insurable-interest',
        term: 'Insurable interest',
        category: 'foundations',
        def: 'You can only insure something you would actually lose money on. Without it, the policy is unenforceable.',
        detail: 'This is why a tenant cannot simply insure the landlord\'s building, and why a landlord asking to be an additional insured on the tenant\'s property policy usually means the coverage has been structured wrong.',
        see: ['indemnity', 'loss-payee', 'additional-insured'],
    },
    {
        id: 'subrogation',
        term: 'Subrogation',
        category: 'foundations',
        essential: true,
        def: 'After paying a claim, the carrier steps into the insured\'s shoes and goes after whoever actually caused it. Any money recovered is theirs, not the insured\'s.',
        detail: 'Subrogation is why carriers care so much about the insured signing away recovery rights before a loss. It is also why a deductible sometimes comes back — recoveries are usually shared with the insured in proportion.',
        see: ['waiver-of-subrogation', 'salvage', 'hold-harmless'],
    },
    {
        id: 'utmost-good-faith',
        term: 'Utmost good faith',
        aka: ['uberrimae fidei'],
        category: 'foundations',
        def: 'Both sides are expected to deal honestly, and the insured is expected to disclose what a carrier would want to know — not only answer the questions asked.',
        see: ['material-misrepresentation', 'application', 'rescission'],
    },
    {
        id: 'material-misrepresentation',
        term: 'Material misrepresentation',
        category: 'underwriting',
        def: 'A false statement on an application about something that would have changed the carrier\'s decision. It can void the policy.',
        detail: 'The test is whether the carrier would have declined or priced differently, not whether the lie caused the loss. This is the practical reason never to "clean up" an application on an insured\'s behalf.',
        see: ['rescission', 'application', 'utmost-good-faith', 'warranty-statement'],
    },
    {
        id: 'rescission',
        term: 'Rescission',
        category: 'claims',
        def: 'The carrier unwinding a policy as if it never existed, usually for misrepresentation on the application. Premium is returned; claims are not paid.',
        confused: 'Not cancellation. Cancellation ends coverage going forward. Rescission erases it from inception, including a loss that already happened.',
        see: ['material-misrepresentation', 'cancellation', 'flat-cancellation'],
    },
    {
        id: 'adverse-selection',
        term: 'Adverse selection',
        category: 'underwriting',
        def: 'The tendency for the people most likely to have a loss to be the most eager to buy coverage.',
        detail: 'It explains a lot of underwriting behaviour that otherwise looks arbitrary — waiting periods, minimum earned premium, and reluctance to write a monoline policy an insured has suddenly gone shopping for.',
        see: ['moral-hazard', 'underwriter'],
    },
    {
        id: 'law-of-large-numbers',
        term: 'Law of large numbers',
        category: 'foundations',
        def: 'The more similar risks a carrier insures, the more accurately it can predict total losses. It is the reason insurance works at all.',
        see: ['loss-ratio', 'actuary'],
    },
    {
        id: 'proximate-cause',
        term: 'Proximate cause',
        category: 'claims',
        def: 'The event that set off the unbroken chain leading to the loss. Coverage usually turns on it rather than on the last thing that happened.',
        detail: 'Property forms often override the common-law rule with anti-concurrent causation wording, which excludes a loss if an excluded peril contributed at all — the flood-versus-wind fight after every hurricane.',
        see: ['anti-concurrent-causation', 'peril'],
    },
    {
        id: 'contribution',
        term: 'Contribution',
        category: 'claims',
        def: 'When two policies cover the same loss, the rule that they share it rather than one paying everything.',
        see: ['other-insurance', 'primary-and-noncontributory', 'stacking'],
    },
    // ------------------------------------------------------------------ policy
    {
        id: 'declarations',
        term: 'Declarations page',
        short: 'Dec page',
        aka: ['dec page', 'declarations', 'decs'],
        category: 'policy',
        essential: true,
        def: 'The front page that says who is insured, for what limits, over what period, at what premium, and which forms are attached.',
        detail: 'The dec page is the fastest read on any policy and the one document worth asking for before quoting a renewal, because the schedule of forms tells you what has been endorsed on or taken away.',
        see: ['policy', 'schedule-of-forms', 'coverage-form', 'endorsement'],
    },
    {
        id: 'policy-jacket',
        term: 'Policy jacket',
        category: 'policy',
        def: 'The generic wrapper of standard conditions and state notices that comes attached to every policy a carrier issues.',
        detail: 'Boilerplate, but not empty — cancellation provisions, legal action limitations and appraisal clauses often live here rather than in the coverage form.',
        see: ['policy', 'conditions'],
    },
    {
        id: 'coverage-form',
        term: 'Coverage form',
        aka: ['coverage part', 'policy form'],
        category: 'policy',
        def: 'The document that actually grants the coverage — the CGL form, the building and personal property form, the business auto form.',
        detail: 'The form number and edition date matter. CG 00 01 04 13 and CG 00 01 12 07 are both "the CGL form" and they do not say the same thing.',
        see: ['iso', 'edition-date', 'endorsement', 'declarations'],
    },
    {
        id: 'insuring-agreement',
        term: 'Insuring agreement',
        category: 'policy',
        essential: true,
        def: 'The sentence in the form that grants the coverage — what the carrier promises to pay for.',
        detail: 'Everything else in a policy either narrows this or clarifies it. Read the insuring agreement first: if a loss does not fall inside it, no exclusion or endorsement matters.',
        see: ['exclusion', 'conditions', 'definitions-section'],
    },
    {
        id: 'exclusion',
        term: 'Exclusion',
        category: 'policy',
        essential: true,
        def: 'Wording that takes something back out of the coverage — a peril, an activity, a type of property, a class of claimant.',
        detail: 'Exclusions exist for three reasons worth telling apart: the risk belongs on another policy (auto, comp), the risk is uninsurable (war, wear and tear), or the carrier will sell it back to you by endorsement. Only the third is negotiable.',
        see: ['insuring-agreement', 'endorsement', 'exception-to-exclusion'],
    },
    {
        id: 'exception-to-exclusion',
        term: 'Exception to an exclusion',
        aka: ['carveback', 'carve-back'],
        category: 'policy',
        def: 'Wording inside an exclusion that gives some of the coverage back. Often called a carveback.',
        detail: 'Some of the most valuable coverage in a CGL lives here — the products-completed operations carveback to the "your work" exclusion for work done by a subcontractor is the whole basis of general contractor coverage.',
        see: ['exclusion', 'your-work'],
    },
    {
        id: 'conditions',
        term: 'Conditions',
        category: 'policy',
        def: 'The rules both sides must follow — how to report a claim, how to cancel, what records to keep, what happens if there is other insurance.',
        detail: 'Breaching a condition can cost coverage even when the loss is squarely covered. Late notice is the one that bites most often.',
        see: ['notice', 'other-insurance', 'duties-after-loss'],
    },
    {
        id: 'definitions-section',
        term: 'Definitions',
        category: 'policy',
        def: 'The section that assigns special meanings to words used in the form. Defined words usually appear in quotation marks or bold.',
        detail: 'When a policy word appears in quotes, stop and go read it. "Auto", "employee", "your work" and "occurrence" all mean something narrower or broader than the ordinary word.',
        see: ['occurrence', 'your-work', 'insuring-agreement'],
    },
    {
        id: 'endorsement',
        term: 'Endorsement',
        aka: ['rider', 'amendatory endorsement'],
        category: 'policy',
        essential: true,
        def: 'An attachment that changes the policy — adding coverage, removing it, adding a party, correcting a detail.',
        detail: 'An endorsement always beats the base form where they conflict. When a carrier says "yes, that is covered", the answerable question is which endorsement number, and whether it is on the policy or only in the email.',
        confused: '"Rider" is the same idea; the word is more common in life and health than in commercial P&C.',
        see: ['coverage-form', 'schedule-of-forms', 'blanket-additional-insured'],
    },
    {
        id: 'schedule-of-forms',
        term: 'Schedule of forms and endorsements',
        category: 'policy',
        def: 'The list on the dec page of every form attached to the policy, by number and edition date.',
        detail: 'Comparing two carriers\' schedules of forms is the fastest honest coverage comparison there is — far more revealing than comparing limits, which are usually identical by design.',
        see: ['declarations', 'endorsement', 'edition-date'],
    },
    {
        id: 'edition-date',
        term: 'Edition date',
        category: 'policy',
        def: 'The date printed after a form number, showing which version of the form it is — CG 00 01 04 13 is the April 2013 edition.',
        detail: 'Carriers do not all move to a new edition at the same time, and some sit deliberately on an older one. A newer edition is not automatically broader.',
        see: ['coverage-form', 'iso'],
    },
    {
        id: 'policy-period',
        term: 'Policy period',
        aka: ['policy term'],
        category: 'policy',
        essential: true,
        def: 'The span the policy is in force, from effective date to expiration date. Almost always twelve months in commercial lines.',
        detail: 'Times matter, not just dates. Most commercial policies incept at 12:01 a.m. standard time at the address on the dec page, which is why a same-day replacement can leave a one-minute gap or a one-minute overlap.',
        see: ['effective-date', 'expiration-date', 'short-term-policy'],
    },
    {
        id: 'effective-date',
        term: 'Effective date',
        aka: ['inception date', 'inception'],
        category: 'policy',
        essential: true,
        def: 'The day and time coverage begins.',
        see: ['policy-period', 'expiration-date', 'binder'],
    },
    {
        id: 'expiration-date',
        term: 'Expiration date',
        aka: ['x-date', 'xdate', 'expiry'],
        category: 'policy',
        essential: true,
        def: 'The day coverage ends. In agency conversation this is the "X-date", and it is the single most valuable fact about a prospect.',
        detail: 'X-dates drive the whole prospecting calendar: you want to be in front of an account sixty to ninety days out, not the week before.',
        see: ['policy-period', 'renewal', 'prospecting'],
    },
    {
        id: 'short-term-policy',
        term: 'Short-term policy',
        category: 'policy',
        def: 'A policy written for less than a full term, usually to line an odd renewal date up with the rest of an account.',
        detail: 'Short-term business is often written on a short-rate basis, so twelve months of coverage bought as two six-month terms costs more than one annual policy.',
        see: ['short-rate', 'common-expiration-date', 'pro-rata'],
    },
    {
        id: 'common-expiration-date',
        term: 'Common expiration date',
        category: 'practice',
        def: 'Lining up every policy on an account to renew on the same day.',
        detail: 'Worth doing: one renewal conversation a year instead of five, no coverage falling through a gap between terms, and a much harder account for a competitor to unpick one policy at a time.',
        see: ['short-term-policy', 'account-rounding'],
    },
    {
        id: 'binder',
        term: 'Binder',
        category: 'policy',
        essential: true,
        def: 'A temporary written contract that puts coverage in force before the policy is issued. It is real coverage, not a promise of it.',
        detail: 'A binder has an expiry of its own — typically thirty to ninety days — and lapses if the policy is not issued. Track binder expiry, not just policy inception.',
        confused: 'Not a quote. A quote is an offer; a binder means coverage is on.',
        see: ['quote', 'binding-authority', 'subjectivities'],
    },
    {
        id: 'quote',
        term: 'Quote',
        category: 'policy',
        essential: true,
        def: 'A carrier\'s offer of terms — coverage, limits and price — for a stated period, usually subject to conditions.',
        detail: 'A quote is only as good as its subjectivities and its expiry date. "Quoted" and "bindable today" are frequently not the same thing.',
        see: ['binder', 'subjectivities', 'indication', 'proposal'],
    },
    {
        id: 'indication',
        term: 'Indication',
        category: 'underwriting',
        def: 'A rough price given before real underwriting, to see whether an account is worth pursuing. Not bindable.',
        detail: 'Never present an indication to an insured as a quote. The number routinely moves once the underwriter sees loss runs and inspection results.',
        see: ['quote', 'subjectivities'],
    },
    {
        id: 'subjectivities',
        term: 'Subjectivities',
        aka: ['subject to'],
        category: 'underwriting',
        essential: true,
        def: 'The things a carrier requires before it will bind or before coverage stays in force — signed application, loss runs, inspection, proof of a safety programme.',
        detail: 'Unmet subjectivities are the most common cause of a policy being flat cancelled after the insured thought they were covered. They should be worked the day the quote arrives, not the day before inception.',
        see: ['quote', 'binder', 'flat-cancellation', 'inspection'],
    },
    {
        id: 'renewal',
        term: 'Renewal',
        category: 'policy',
        essential: true,
        def: 'Continuing coverage for another term, either by issuing a new policy or by the carrier offering terms the insured accepts.',
        detail: 'A renewal is a new contract. Forms, limits and exclusions can all change, and "renewed as expiring" should be checked rather than believed.',
        see: ['non-renewal', 'expiration-date', 'remarket'],
    },
    {
        id: 'non-renewal',
        term: 'Non-renewal',
        category: 'policy',
        def: 'The carrier declining to offer another term. Coverage runs to expiry and then stops.',
        detail: 'Most states require advance notice — commonly 45 to 60 days — and the notice requirement is the insured\'s practical protection, since it is the only thing that gives you time to remarket.',
        confused: 'Not cancellation. Cancellation ends a policy mid-term; non-renewal simply declines the next one.',
        see: ['cancellation', 'renewal', 'remarket'],
    },
    {
        id: 'cancellation',
        term: 'Cancellation',
        category: 'policy',
        essential: true,
        def: 'Ending a policy before its expiration date, by the insured or by the carrier.',
        detail: 'Carrier cancellation is heavily restricted after the first sixty days — typically only for non-payment, fraud, or a material change in the risk — and always requires written notice to the first named insured.',
        see: ['flat-cancellation', 'pro-rata', 'short-rate', 'first-named-insured'],
    },
    {
        id: 'flat-cancellation',
        term: 'Flat cancellation',
        category: 'policy',
        def: 'Cancelling a policy back to its inception date as though it never attached, with all premium returned.',
        detail: 'Only available when there has been no exposure and usually no claim — most carriers will not flat cancel once the policy has been in force, and many will not at all if a certificate has been issued.',
        see: ['cancellation', 'return-premium', 'minimum-earned-premium'],
    },
    {
        id: 'pro-rata',
        term: 'Pro rata',
        category: 'money',
        def: 'Returning or charging premium in exact proportion to the time involved. Six months into a $12,000 annual policy, cancelling pro rata returns $6,000.',
        confused: 'Short rate returns less than pro rata — the carrier keeps a penalty for the early cancellation.',
        see: ['short-rate', 'return-premium', 'earned-premium'],
    },
    {
        id: 'short-rate',
        term: 'Short rate',
        category: 'money',
        def: 'A cancellation calculation that returns less than the time-proportional amount, keeping a penalty for the carrier.',
        detail: 'Usually applied when the insured cancels, not when the carrier does. Ten percent of the unearned premium is a common penalty, but it varies by carrier and by state.',
        see: ['pro-rata', 'minimum-earned-premium', 'cancellation'],
    },
    {
        id: 'reinstatement',
        term: 'Reinstatement',
        category: 'policy',
        def: 'Putting a cancelled policy back in force.',
        detail: 'Watch for a lapse in coverage: some reinstatements are effective from the reinstatement date forward, leaving a gap for which nobody is on risk. Others require a no-loss letter first.',
        see: ['no-loss-letter', 'cancellation', 'lapse'],
    },
    {
        id: 'lapse',
        term: 'Lapse',
        aka: ['gap in coverage'],
        category: 'policy',
        def: 'A period with no coverage in force, usually because a policy expired or cancelled before the next one attached.',
        detail: 'A lapse is worse than it looks on claims-made business: the retroactive date on the replacement policy is often reset to the new inception, wiping out coverage for everything that happened before it.',
        see: ['retroactive-date', 'claims-made', 'prior-acts'],
    },
    {
        id: 'occurrence-form',
        term: 'Occurrence form',
        category: 'policy',
        essential: true,
        def: 'Covers injury or damage that happens during the policy period, no matter when the claim is finally made — even years later.',
        detail: 'The policy in force when the harm occurred is the one that responds, which is why an insured needs to keep old dec pages indefinitely rather than for seven years.',
        confused: 'Claims-made covers the claim being made during the period, which is a completely different trigger.',
        see: ['claims-made', 'occurrence', 'retroactive-date'],
    },
    {
        id: 'claims-made',
        term: 'Claims-made form',
        category: 'policy',
        essential: true,
        def: 'Covers claims first made against the insured during the policy period, provided the act happened after the retroactive date.',
        detail: 'Two dates control everything: the retroactive date at the back end, and the reporting deadline at the front. Break the chain of continuous renewals and coverage for years of past work can vanish at once.',
        confused: 'Occurrence form triggers on when the harm happened, not when the claim arrives.',
        see: ['retroactive-date', 'tail-coverage', 'prior-acts', 'occurrence-form'],
    },
    {
        id: 'claims-made-and-reported',
        term: 'Claims-made and reported',
        category: 'policy',
        def: 'A stricter claims-made trigger: the claim must be both made against the insured and reported to the carrier within the same policy period.',
        detail: 'A claim made in the last week of the term and reported three weeks later is uncovered under this wording, even though a plain claims-made form would have paid it. Worth spotting on a quote comparison.',
        see: ['claims-made', 'notice', 'tail-coverage'],
    },
    {
        id: 'retroactive-date',
        term: 'Retroactive date',
        aka: ['retro date'],
        category: 'policy',
        essential: true,
        def: 'On a claims-made policy, the earliest date an act can have happened and still be covered. Anything before it is excluded.',
        detail: 'Never let a retro date move forward at renewal or on a change of carrier — that is the single most damaging thing that can quietly happen on a professional liability account. "Full prior acts" means no retro date at all.',
        see: ['claims-made', 'prior-acts', 'tail-coverage', 'lapse'],
    },
    {
        id: 'prior-acts',
        term: 'Prior acts',
        aka: ['full prior acts', 'nose coverage'],
        category: 'policy',
        def: 'Coverage for work done before the current policy incepted. "Full prior acts" means no retroactive date limits it.',
        confused: 'Prior acts (sometimes "nose") looks backwards from a new policy. Tail looks forwards from an expiring one.',
        see: ['retroactive-date', 'tail-coverage', 'claims-made'],
    },
    {
        id: 'tail-coverage',
        term: 'Tail coverage',
        aka: ['extended reporting period', 'erp', 'run-off'],
        category: 'policy',
        essential: true,
        def: 'An extension that lets an insured report claims after a claims-made policy ends, for acts committed while it was in force.',
        detail: 'Basic tail is usually automatic and short — 30 or 60 days. Supplemental tail is bought, often at 100–300% of the expiring premium, and there is normally a strict window (commonly 30–60 days after expiry) to elect it. Miss the window and it is gone.',
        see: ['claims-made', 'retroactive-date', 'prior-acts'],
    },
    // ---------------------------------------------------------------- limits
    {
        id: 'limit-of-liability',
        term: 'Limit of liability',
        aka: ['limit', 'limits'],
        category: 'limits',
        essential: true,
        def: 'The most the carrier will pay. Policies usually carry several limits that apply to different things at once.',
        see: ['occurrence-limit', 'aggregate-limit', 'sublimit', 'csl'],
    },
    {
        id: 'occurrence-limit',
        term: 'Per occurrence limit',
        aka: ['each occurrence limit'],
        category: 'limits',
        essential: true,
        def: 'The most payable for any one event, no matter how many claimants it produced.',
        detail: 'Whether several injuries are one occurrence or many is worth real money and is decided by the definition of occurrence, not by common sense — a single defective batch can be one occurrence across hundreds of claims.',
        see: ['aggregate-limit', 'occurrence', 'batch-clause'],
    },
    {
        id: 'aggregate-limit',
        term: 'Aggregate limit',
        category: 'limits',
        essential: true,
        def: 'The most payable in total across the whole policy period, however many occurrences there are.',
        detail: 'A standard CGL carries two aggregates: a general aggregate and a separate products-completed operations aggregate. Once an aggregate is exhausted the policy is finished for the term, with no notice to anyone holding a certificate.',
        see: ['occurrence-limit', 'products-completed-aggregate', 'per-project-aggregate'],
    },
    {
        id: 'per-project-aggregate',
        term: 'Per project aggregate',
        category: 'limits',
        def: 'An endorsement giving each job site its own general aggregate, so one bad project cannot exhaust the limit for all the others.',
        detail: 'Frequently demanded in construction contracts and frequently missing from the policy. It must be endorsed on — CG 25 03 — and carriers price it.',
        see: ['aggregate-limit', 'per-location-aggregate', 'additional-insured'],
    },
    {
        id: 'per-location-aggregate',
        term: 'Per location aggregate',
        category: 'limits',
        def: 'The same idea as a per project aggregate, applied to each premises rather than each job.',
        detail: 'CG 25 04. Common on habitational and multi-site retail accounts.',
        see: ['per-project-aggregate', 'aggregate-limit'],
    },
    {
        id: 'products-completed-aggregate',
        term: 'Products-completed operations aggregate',
        aka: ['products aggregate', 'pco aggregate'],
        category: 'limits',
        def: 'A separate aggregate limit for claims arising from the insured\'s products, or from work after it has been finished.',
        detail: 'It sits apart from the general aggregate on purpose: a contractor with a bad completed job does not want that eating the limit protecting their ongoing work.',
        see: ['products-completed-operations', 'aggregate-limit'],
    },
    {
        id: 'sublimit',
        term: 'Sublimit',
        category: 'limits',
        essential: true,
        def: 'A smaller limit inside a bigger one, applying to a particular coverage. It does not add to the policy limit — it caps part of it.',
        detail: 'Sublimits are where coverage quietly disappears: $25,000 of employee dishonesty inside a $2m package, $10,000 of pollution clean-up, $5,000 of electronic data. Comparing headline limits between two quotes without comparing sublimits is not a comparison.',
        see: ['limit-of-liability', 'coverage-extension'],
    },
    {
        id: 'csl',
        term: 'Combined single limit',
        short: 'CSL',
        category: 'limits',
        def: 'One limit covering both bodily injury and property damage together, rather than separate amounts for each.',
        confused: 'Split limits state three numbers — per person, per accident, and property damage. A $1m CSL is generally better than 250/500/100 even though the numbers look bigger split out.',
        see: ['split-limits', 'limit-of-liability'],
    },
    {
        id: 'split-limits',
        term: 'Split limits',
        category: 'limits',
        def: 'Separate limits for bodily injury per person, bodily injury per accident, and property damage — written as 100/300/50.',
        detail: 'Still common on personal auto and small commercial auto. The per-person cap is the one that hurts, since a single badly injured claimant can exceed it while the per-accident limit goes unused.',
        see: ['csl', 'limit-of-liability'],
    },
    {
        id: 'deductible',
        term: 'Deductible',
        category: 'limits',
        essential: true,
        def: 'The amount the insured pays on each loss before the carrier pays anything.',
        detail: 'On a liability policy, check whether the deductible applies to defence costs as well as damages, and whether the carrier pays first and bills back or the insured pays up front.',
        confused: 'A self-insured retention is not a deductible — see that entry, because the difference decides who runs the defence.',
        see: ['sir', 'aggregate-deductible', 'percentage-deductible'],
    },
    {
        id: 'sir',
        term: 'Self-insured retention',
        short: 'SIR',
        category: 'limits',
        def: 'An amount the insured pays and handles themselves before the policy responds at all.',
        detail: 'The practical difference from a deductible: with an SIR the carrier usually has no duty to defend until the retention is exhausted, so the insured must hire and pay counsel from the first dollar. Excess policies also sit above an SIR differently than above a deductible.',
        confused: 'A deductible is subtracted from what the carrier pays. An SIR must be spent before the carrier is involved.',
        see: ['deductible', 'duty-to-defend', 'excess-liability'],
    },
    {
        id: 'aggregate-deductible',
        term: 'Aggregate deductible',
        category: 'limits',
        def: 'A cap on the total deductibles an insured will pay across the whole policy period, however many losses there are.',
        see: ['deductible', 'sir'],
    },
    {
        id: 'percentage-deductible',
        term: 'Percentage deductible',
        aka: ['wind deductible', 'named storm deductible', 'hurricane deductible'],
        category: 'property',
        def: 'A deductible expressed as a percentage of the insured value rather than a flat dollar amount — typically for wind, hail, named storms or earthquake.',
        detail: 'A 5% wind deductible on a $4m building is $200,000, which is a shock to an insured who has been told "the deductible is five percent". Always convert it to dollars in the proposal.',
        see: ['deductible', 'tiv', 'named-storm'],
    },
    {
        id: 'coinsurance',
        term: 'Coinsurance',
        category: 'property',
        essential: true,
        def: 'A property clause requiring the insured to carry a limit at least equal to a stated percentage of the property\'s value — usually 80, 90 or 100%. Carry less and every claim, even a small one, is reduced proportionally.',
        detail: 'The penalty is the part people miss: the reduction applies to partial losses, which are almost all losses. Insuring a $1m building for $600,000 with an 80% clause means a $100,000 fire pays $75,000, less the deductible.',
        confused: 'Nothing to do with health-insurance coinsurance, which is a cost share on every bill.',
        see: ['agreed-value', 'tiv', 'margin-clause', 'replacement-cost'],
    },
    {
        id: 'agreed-value',
        term: 'Agreed value',
        aka: ['agreed amount'],
        category: 'property',
        def: 'An option that suspends the coinsurance clause, because the carrier and insured have agreed the value up front — usually on the strength of a signed statement of values.',
        detail: 'The cleanest fix for coinsurance anxiety on a large property schedule. It normally requires an annual statement of values and lapses if not renewed with one.',
        see: ['coinsurance', 'statement-of-values', 'margin-clause'],
    },
    {
        id: 'margin-clause',
        term: 'Margin clause',
        category: 'property',
        def: 'A cap limiting recovery at any location to a stated percentage — often 110 or 125% — of the value reported for that location on the statement of values.',
        detail: 'It is how carriers stop blanket limits being used to cover an under-reported building. Under-report a location and the blanket limit will not save you.',
        see: ['blanket-limit', 'statement-of-values', 'coinsurance'],
    },
    {
        id: 'blanket-limit',
        term: 'Blanket limit',
        category: 'property',
        def: 'One limit covering several buildings or several categories of property together, rather than a separate limit for each.',
        detail: 'The advantage is flexibility when one location turns out to be worth more than reported. Margin clauses and coinsurance both erode that advantage, so read them together.',
        confused: 'Scheduled coverage lists each item with its own limit. Blanket pools them.',
        see: ['scheduled-coverage', 'margin-clause', 'statement-of-values'],
    },
    {
        id: 'scheduled-coverage',
        term: 'Scheduled coverage',
        category: 'property',
        def: 'Each item or location listed individually with its own limit.',
        detail: 'More precise and usually cheaper than blanket, but unforgiving — property not on the schedule is not covered, and newly acquired items rely on a limited extension until reported.',
        see: ['blanket-limit', 'newly-acquired-property'],
    },
    {
        id: 'defense-within-limits',
        term: 'Defense within limits',
        aka: ['eroding limits', 'wasting limits', 'burning limits'],
        category: 'limits',
        def: 'A policy where legal defence costs come out of the limit rather than being paid in addition to it.',
        detail: 'Standard on most professional liability, D&O and cyber. A $1m limit with a $400,000 defence leaves $600,000 to settle with. On a CGL, defence is normally outside the limit, which is one of the biggest structural differences between the two.',
        see: ['supplementary-payments', 'duty-to-defend', 'sir'],
    },
    {
        id: 'supplementary-payments',
        term: 'Supplementary payments',
        category: 'limits',
        def: 'Costs a liability policy pays on top of the limit — defence costs, bail bonds, appeal bonds, and interest on a judgment.',
        detail: 'This is why a CGL limit goes further than the same number on a claims-made professional policy: defence is additional here and erosive there.',
        see: ['defense-within-limits', 'duty-to-defend'],
    },
    {
        id: 'coverage-extension',
        term: 'Coverage extension',
        aka: ['additional coverage'],
        category: 'policy',
        def: 'A small amount of extra coverage built into a form for a related exposure — debris removal, newly acquired property, fire department service charge.',
        detail: 'Extensions are almost always sublimited and are the main reason two "identical" property forms are not identical. They are also the cheapest place to find value on a comparison.',
        see: ['sublimit', 'coverage-form'],
    },
    {
        id: 'stacking',
        term: 'Stacking',
        category: 'limits',
        def: 'Adding together the limits of more than one policy period or more than one vehicle to increase the total recovery.',
        detail: 'Most commercial forms contain anti-stacking language. It matters most on continuous or progressive damage — construction defect, pollution — where several policy years may be triggered.',
        see: ['anti-stacking', 'continuous-trigger', 'other-insurance'],
    },
    {
        id: 'anti-stacking',
        term: 'Anti-stacking provision',
        aka: ['non-cumulation clause'],
        category: 'limits',
        def: 'Wording that stops limits from multiple policy periods being added together for the same loss.',
        see: ['stacking', 'continuous-trigger'],
    },
];
